/// <reference types="vite/client" />
import posthog from 'posthog-js';
import React, { useEffect, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router';

import './index.css';
import App from './App';
import { AnalyticsProvider } from './providers';
import Loading from './components/layout/loading';
import { initialisePostHog } from './posthog/initialisePostHog';

const cspPatterns = [
  'img-src',
  'connect-src',
  'script-src-elem',
  'Content Security Policy',
  'google.com.au',
  'www.google.com',
  'stats.g.doubleclick.net',
  'www.googletagmanager.com',
];

initialisePostHog();

Sentry.init({
  dsn: 'https://19127fa0231bcccc0c6ce736f0e6d12e@o4506737640144896.ingest.sentry.io/4506744414404608',
  environment: import.meta.env.MODE || 'development',
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: false,
    }),
    Sentry.extraErrorDataIntegration({ depth: 7 }),
    posthog.sentryIntegration({ organization: 'https://candor-medical.sentry.io', projectId: 4506744414404608 }),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.01, //  Capture 1% of transactions
  // Session Replay
  replaysSessionSampleRate: 0.01, // Capture 1% of sessions
  replaysOnErrorSampleRate: 1.0, // Capture 100% of errors (Note: we may reduce this if cost is too high)

  // Filter high-volume CSP violations and browser noise
  ignoreErrors: [
    // CSP violations - these create massive noise and often come from extensions/third-party scripts
    /SecurityPolicyViolationEvent/,
    /Content Security Policy/,
    /script-src-elem/,
    /img-src/,
    /connect-src/,
    /Blocked.*googletagmanager/,
    /Blocked.*google\.com(?:$|[/\s:?])/,
  ],

  beforeSend(event) {
    // Filter CSP violation reports (they come as security reports from browser)
    if (event.exception?.values?.[0]?.type === 'SecurityPolicyViolationEvent') {
      return null;
    }

    // Filter if it's a CSP-related message
    const message = event.message || '';

    if (cspPatterns.some((pattern) => message.includes(pattern))) {
      return null;
    }

    // Filter CSP violations from event breadcrumbs and extra data
    const violationDirective = event.extra?.['Violation directive'];
    if (
      typeof violationDirective === 'string' &&
      (violationDirective.includes('img-src') || violationDirective.includes('connect-src'))
    ) {
      return null;
    }

    return event;
  },
});

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root container (#root) not found');
}

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Suspense fallback={<Loading />}>
      <AnalyticsProvider>
        <App />
      </AnalyticsProvider>
    </Suspense>
  </React.StrictMode>,
);
